import * as React from "react";
import { Button, Stack, Chip, Typography } from "@mui/material";
import {
  ContentLayout,
  CustomDetailsList,
  HeaderBreadcrumbs,
  Loader,
  useAppConfig,
  useFormatAmount,
} from "../../../shared";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useClientAccountDetails } from "../hooks";
import { LedgerAccountsList } from "../../Configurations/components";
import {
  AddressesList,
  SettlementDescription,
  StatementsList,
} from "../components";
import { ClientAccountTransactions } from "./ClientAccountTransactions";
import InputControl from "../../../shared/components/InputControl";

export const ClientAccountDetailsList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { taxIdTerm } = useAppConfig();
  const formatAmount = useFormatAmount();

  const {
    state,
    isStoreAdmin,
    globalData,
    isEditable,
    HandleClientAccountStatus,
  } = useClientAccountDetails({ id: id });

  const { details, isLoading } = state;

  const column = [
    {
      title: "Account No",
      accessor: "number",
    },
    {
      title: taxIdTerm,
      accessor: "abn_number",
    },
    {
      title: "Store Name",
      accessor: "store_name",
    },
    {
      title: "Credit Limit",
      accessor: ({ credit_limit, due_amount }) => (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <span>{formatAmount(credit_limit)}</span>
          <span style={{ fontWeight: 600, color: "#C07302" }}>
            Total Due Amount : {formatAmount(due_amount)}
          </span>
        </Stack>
      ),
    },
    {
      title: "Restrict Credit",
      accessor: ({ restrict_usage }) =>
        restrict_usage === 1 ? "Restricted" : "Not Restricted",
    },
    {
      title: "Statement Frequency",
      accessor: ({ bill_day, bill_duration, bill_period, bill_date }) => {
        return (
          <Stack direction="row" spacing={1}>
            <SettlementDescription
              bill_day={bill_day}
              bill_duration={bill_duration}
              bill_period={bill_period}
              bill_date={bill_date}
              globalData={globalData}
            />
          </Stack>
        );
      },
    },
    {
      title: "Due Date",
      accessor: ({ due_days }) => `${due_days} Days from Statement Date`,
    },
    {
      title: "Auto-send statements to ",
      accessor: ({ statement_emails }) => {
        return (
          <>
            <Stack
              sx={{
                flexDirection: "row",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {statement_emails?.map((data, index) => {
                return (
                  <Chip
                    key="index"
                    sx={{
                      borderRadius: "12px",
                      background: "rgba(0, 0, 0, 0.16)",
                    }}
                    label={data}
                    variant="filled"
                    size="small"
                  />
                );
              })}
            </Stack>
          </>
        );
      },
    },
    {
      title: "Auto-send Invoices to",
      accessor: ({ invoice_emails }) => {
        return (
          <>
            <Stack
              sx={{
                flexDirection: "row",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {invoice_emails?.map((data, index) => {
                return (
                  <Chip
                    key="index"
                    sx={{
                      borderRadius: "12px",
                      background: "rgba(0, 0, 0, 0.16)",
                    }}
                    label={data}
                    variant="filled"
                    size="small"
                  />
                );
              })}
            </Stack>
          </>
        );
      },
    },
  ];

  const tabs = React.useMemo(() => {
    return [
      {
        label: "Details",
        value: "1",
        panel: (
          <>
            {isLoading ? (
              <Loader />
            ) : (
              <>
                <CustomDetailsList
                  columns={column}
                  data={details}
                  title="Details"
                  headerRightContent={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography>Account Status</Typography>
                      {isEditable && (
                        <InputControl
                          type="switch"
                          checked={details?.status === 1 ? true : false}
                          onChange={() => {
                            HandleClientAccountStatus(id);
                          }}
                        />
                      )}
                      {isEditable && (
                        <Button
                          type="button"
                          size="medium"
                          variant="text"
                          flex-direction="right"
                          onClick={() =>
                            isStoreAdmin
                              ? navigate(
                                  `/store-admin/client-account-v2/edit/${id}`,
                                )
                              : navigate(`/client-account-v2/edit/${id}`)
                          }
                        >
                          Edit
                        </Button>
                      )}
                    </Stack>
                  }
                />
              </>
            )}
          </>
        ),
      },
      {
        label: "Transactions",
        value: "2",
        panel: (
          <ClientAccountTransactions invoiceEmails={details?.invoice_emails} />
        ),
      },
      {
        label: "Addresses",
        value: "4",
        panel: <AddressesList />,
      },
      {
        label: "Statements",
        value: "5",
        panel: <StatementsList />,
      },
      {
        label: "Accounts",
        value: "3",
        hidden: isStoreAdmin,
        panel: <LedgerAccountsList type="clientaccount" />,
      },
    ];
  }, [isStoreAdmin, column, details, isLoading]);

  return (
    <ContentLayout
      title={details?.account_name}
      active={details?.status ? "Active" : "Inactive"}
      breadcrumbs={
        <>
          <HeaderBreadcrumbs
            links={[
              {
                name: "Dashboard",
                href: isStoreAdmin ? "/store-admin/" : "/",
              },
              {
                name: "Client Account",
                href: isStoreAdmin
                  ? "/store-admin/client-account-v2"
                  : `/client-account-v2`,
              },
              { name: details.account_name },
            ]}
          />
        </>
      }
      rightContent={
        <>
          {isStoreAdmin && (
            <Link
              to={`settlement`}
              state={details?.customer_name}
              style={{ textDecoration: "none" }}
            >
              <Button
                type="button"
                size="medium"
                variant="contained"
                sx={{ minWidth: "150px", ml: "10px" }}
              >
                Settlement /Deposit
              </Button>
            </Link>
          )}
        </>
      }
      tabProps={{
        label: "clientAccDetails",
        list: tabs,
      }}
    />
  );
};
